import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, ShieldCheck, LogOut, LayoutDashboard, Truck, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/lib/categories";

const links = [
  { to: "/", label: "Home" },
  { to: "/weekly-drop", label: "This Week" },
  { to: "/shop/clones", label: "Clones" },
  { to: "/shop/seeds", label: "Seeds" },
  { to: "/shop", label: "Genetics" },
  { to: "/coa", label: "COA" },
  { to: "/clone-care", label: "Clone Care" },
  { to: "/about", label: "About" },
];

function ClonesMenu({ categories }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="nav-clones-dropdown"
    >
      <NavLink
        to="/shop/clones"
        data-testid="nav-clones"
        className={({ isActive }) =>
          `flex items-center gap-1 text-sm tracking-wide transition-colors ${
            isActive ? "text-emerald-400" : "text-slate-300 hover:text-white"
          }`
        }
      >
        Clones <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>
      <div
        className={`absolute left-0 top-full pt-3 w-56 transition-opacity duration-200 ${
          open ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <div className="bg-[#0A0A0A] border border-emerald-900/50 shadow-xl shadow-black/50 py-2">
          <NavLink
            to="/shop/clones"
            data-testid="nav-clones-all"
            className="block px-4 py-2 text-sm text-slate-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
          >
            All Clones
          </NavLink>
          {categories.length > 0 && <div className="my-1 border-t border-white/5" />}
          {categories.map((c) => (
            <NavLink
              key={c.slug}
              to={`/shop/clones/category/${c.slug}`}
              data-testid={`nav-clones-cat-${c.slug}`}
              className="flex items-center justify-between px-4 py-2 text-sm text-slate-300 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
            >
              <span className="uppercase tracking-wide">{c.name}</span>
              <span className="text-slate-600 text-xs">{c.clone_count}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [menu, setMenu] = useState(false);
  const [mobileClones, setMobileClones] = useState(false);
  const categories = useCategories();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-[#050505]/70 backdrop-blur-xl border-b border-white/10">
      <div
        data-testid="shipping-banner"
        className="flex items-center justify-center gap-2 bg-emerald-500 text-[#050505] py-1.5 px-4 text-center"
      >
        <Truck size={14} strokeWidth={2.2} />
        <span className="text-[0.7rem] sm:text-xs font-semibold tracking-wide uppercase">
          Free Next-Day Shipping on All Orders · Discreet Packaging
        </span>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img
              src="/dglogo.png"
              alt="Dark Growers Genetics"
              className="h-16 lg:h-20 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) =>
              l.label === "Clones" ? (
                <ClonesMenu key={l.to} categories={categories} />
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm tracking-wide transition-colors ${
                      isActive ? "text-emerald-400" : "text-slate-300 hover:text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-5">
            {user ? (
              <div className="hidden sm:flex items-center gap-4">
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    className="flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300"
                  >
                    <LayoutDashboard size={16} /> Admin
                  </Link>
                )}
                <Link
                  to="/account"
                  data-testid="nav-account"
                  className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"
                >
                  <User size={16} /> {user.name.split(" ")[0]}
                </Link>
                <button
                  onClick={doLogout}
                  data-testid="logout-btn"
                  className="text-slate-500 hover:text-white transition-colors"
                  title="Log out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="hidden sm:flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"
              >
                <User size={16} /> Login
              </Link>
            )}

            <button
              data-testid="cart-button"
              onClick={() => setOpen(true)}
              className="relative text-slate-200 hover:text-emerald-400 transition-colors"
            >
              <ShoppingBag size={22} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bg-emerald-500 text-[#050505] text-[0.6rem] font-semibold w-5 h-5 flex items-center justify-center rounded-full"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              className="lg:hidden text-slate-200"
              data-testid="mobile-menu-toggle"
              onClick={() => setMenu((m) => !m)}
            >
              {menu ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {menu && (
        <div className="lg:hidden border-t border-white/10 bg-[#050505] px-6 py-6 space-y-4">
          {links.map((l) =>
            l.label === "Clones" ? (
              <div key={l.to}>
                <div className="flex items-center justify-between">
                  <NavLink
                    to="/shop/clones"
                    onClick={() => setMenu(false)}
                    data-testid="mobile-nav-clones"
                    className="block text-slate-300 hover:text-white text-base"
                  >
                    Clones
                  </NavLink>
                  <button
                    data-testid="mobile-clones-toggle"
                    onClick={() => setMobileClones((v) => !v)}
                    className="text-slate-400 p-1"
                    aria-label="Toggle clone categories"
                  >
                    <ChevronDown size={18} className={`transition-transform ${mobileClones ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobileClones && (
                  <div className="mt-2 ml-3 pl-3 border-l border-emerald-900/50 space-y-2.5" data-testid="mobile-clones-submenu">
                    <NavLink to="/shop/clones" onClick={() => setMenu(false)} className="block text-slate-400 hover:text-emerald-400 text-sm">
                      All Clones
                    </NavLink>
                    {categories.map((c) => (
                      <NavLink
                        key={c.slug}
                        to={`/shop/clones/category/${c.slug}`}
                        onClick={() => setMenu(false)}
                        data-testid={`mobile-nav-clones-cat-${c.slug}`}
                        className="block text-slate-400 hover:text-emerald-400 text-sm uppercase tracking-wide"
                      >
                        {c.name} <span className="text-slate-600">({c.clone_count})</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setMenu(false)}
                className="block text-slate-300 hover:text-white text-base"
              >
                {l.label}
              </NavLink>
            )
          )}
          <div className="pt-4 border-t border-white/10 space-y-3">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setMenu(false)} className="block text-emerald-400">
                    Admin Dashboard
                  </Link>
                )}
                <Link to="/account" onClick={() => setMenu(false)} className="block text-slate-300">
                  My Account
                </Link>
                <button onClick={doLogout} className="block text-slate-500">
                  Log out
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMenu(false)} className="block text-slate-300">
                Login / Register
              </Link>
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-center gap-2 bg-emerald-950/40 border-b border-emerald-900/40 py-1.5">
        <ShieldCheck size={13} className="text-emerald-500" />
        <span className="text-[0.65rem] tracking-wide text-emerald-300/80">
          Lab-tested clean · HLVD · TMV · CMV · TSWV · INSV · &lt;0.3% Δ9 THC
        </span>
      </div>
    </header>
  );
}
